import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, AbstractControl, FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { SharedFilterDataService } from 'src/app/core/services/shared-filter-data.service';
import { Observable } from 'rxjs';
import { VirtualAutocompleteComponent } from 'src/app/shared/components/virtual-autocomplete/virtual-autocomplete.component';
import { VirtualAutocompleteConfig } from 'src/app/shared/components/virtual-autocomplete/virtual-autocomplete.interfaces';
import { StatusAvis } from '@models/enums';
import { DemandeStatusService } from 'src/app/core/services/demande-status.service';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { TypeRecrutement } from '@models/domains/recruitment';

@Component({
    selector: 'app-filter-controls',
    imports: [
        CommonModule,
        MatIconModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        VirtualAutocompleteComponent
    ],
    templateUrl: './filter-controls.component.html',
    styleUrls: ['./filter-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterControlsComponent implements OnInit {
  form_filtre_demande!: FormGroup;
  @Output() filterApplied = new EventEmitter<any>();

  @Input() showDirection: boolean = false;
  @Input() showService: boolean = false;
  @Input() showAgentRenouvele: any = null;
  @Input() showNumeroPoste: boolean = false;
  showDetenteurPoste = input(false);
  @Input() showTypeRecrutement: boolean = false;
  @Input() showTypeRenouvellement: boolean = false;

  public StatusAvis = StatusAvis;
  public statusOptions: { value: StatusAvis; viewValue: string }[] = [];

  // Data observables for autocomplete components
  agentsList$!: Observable<any[]>;
  agentsDemandesList$!: Observable<any[]>;
  servicesList$!: Observable<any[]>;
  directionsList$!: Observable<any[]>;
  postesList$!: Observable<any[]>;
  typeRecrutementList$!: Observable<TypeRecrutement[]>;
  typeRenouvellementList$!: Observable<any[]>;

  // Configuration for autocomplete components
  agentConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (agent: any) => {
      if (!agent) return '';
      const nom = agent.nom || '';
      const prenom = agent.prenom || '';
      return `${nom} ${prenom}`.trim();
    },
    filterFn: (agents: any[], searchText: string) => {
      if (!searchText) return agents;
      const filterValue = searchText.toLowerCase().split(' ').join('');
      return agents.filter((agent: any) => {
        if (!agent) return false;
        const nom = agent.nom || '';
        const prenom = agent.prenom || '';
        return (nom + prenom).toLowerCase().split(' ').join('').includes(filterValue);
      });
    }
  };

  serviceConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (service: any) => (service ? service.libelle : ''),
    filterFn: (services: any[], searchText: string) => {
      if (!searchText) return services;
      const filterValue = searchText.toLowerCase();
      return services.filter((service: any) => service.libelle.toLowerCase().includes(filterValue));
    },
    itemSize: 48
  };

  directionConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (direction: any) => (direction ? direction.libelle : ''),
    filterFn: (directions: any[], searchText: string) => {
      if (!searchText) return directions;
      const filterValue = searchText.toLowerCase();
      return directions.filter((direction: any) => direction.libelle.toLowerCase().includes(filterValue));
    },
    itemSize: 48
  };

  posteConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (poste: any) => (poste ? poste.numero.toString() : ''),
    filterFn: (postes: any[], searchText: string) => {
      if (!searchText) return postes;
      const filterValue = searchText.toLowerCase();
      return postes.filter((poste: any) => {
        const numeroStr = poste.numero ? poste.numero.toString() : '';
        return numeroStr.toLowerCase().includes(filterValue);
      });
    },
    itemSize: 48
  };

  typeRecrutementConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (type: any) => (type ? type.libelle : ''),
    filterFn: (types: any[], searchText: string) => {
      if (!searchText) return types;
      const filterValue = searchText.toLowerCase();
      return types.filter((type: any) => type.libelle.toLowerCase().includes(filterValue));
    },
    itemSize: 48
  };

  typeRenouvellementConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (type: any) => (type ? type.libelle : ''),
    filterFn: (types: any[], searchText: string) => {
      if (!searchText) return types;
      const filterValue = searchText.toLowerCase();
      return types.filter((type: any) => type.libelle.toLowerCase().includes(filterValue));
    },
    itemSize: 48
  };

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sharedFilterDataService: SharedFilterDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly demandeStatusService: DemandeStatusService,
    private readonly referentielsService: ReferentielsService
  ) {}

  // #region Getters for Form Controls (Optimization for Template Bindings)
  get emetteurControl() {
    return this.form_filtre_demande.get('emetteur') as FormControl;
  }

  get directionControl() {
    return this.form_filtre_demande.get('direction') as FormControl;
  }

  get serviceFonctionnelControl() {
    return this.form_filtre_demande.get('serviceFonctionnel') as FormControl;
  }

  get numeroPosteControl() {
    return this.form_filtre_demande.get('numeroPoste') as FormControl;
  }

  get detenteurPosteControl() {
    return this.form_filtre_demande.get('detenteurPoste') as FormControl;
  }

  get agentRenouveleControl() {
    return this.form_filtre_demande.get('agentRenouvele') as FormControl;
  }

  get statutControl() {
    return this.form_filtre_demande.get('statut') as FormControl;
  }

  get typeRecrutementControl() {
    return this.form_filtre_demande.get('typeRecrutement') as FormControl;
  }

  get typeRenouvellementControl() {
    return this.form_filtre_demande.get('typeRenouvellement') as FormControl;
  }
  // #endregion

  ngOnInit() {
    this.form_filtre_demande = this.formBuilder.group({
      emetteur: [''],
      direction: [''],
      serviceFonctionnel: [''],
      numeroPoste: [''],
      detenteurPoste: [''],
      agentRenouvele: [''],
      dateCreation: [''],
      dateCreationStart: [''],
      dateCreationEnd: [''],
      statut: [''],
      typeRecrutement: [''],
      typeRenouvellement: ['']
    });

    this.statusOptions = Object.values(StatusAvis).map((status) => ({
      value: status,
      viewValue: this.demandeStatusService.getStatusText(status)
    }));

    // Initialize data observables for autocomplete components
    this.agentsList$ = this.sharedFilterDataService.loadAgents();
    this.agentsDemandesList$ = this.sharedFilterDataService.loadAgentsDemandes();

    if (this.showService) {
      this.servicesList$ = this.sharedFilterDataService.loadServices();
    }

    if (this.showDirection) {
      this.directionsList$ = this.sharedFilterDataService.loadDirections();
    }

    if (this.showNumeroPoste) {
      this.postesList$ = this.sharedFilterDataService.loadPostes();
    }

    if (this.showTypeRecrutement) {
      this.typeRecrutementList$ = this.referentielsService.getTypeRecrutement();
    }

    if (this.showTypeRenouvellement) {
      this.typeRenouvellementList$ = this.referentielsService.getTypeRenouvellement();
    }
  }

  /**
   * Clears a filter control value - still needed for mat-select clear button
   * @param event The event to stop propagation
   * @param control The AbstractControl to clear
   */
  clearFilterControl(event: Event, control: AbstractControl | null) {
    event.stopPropagation();
    if (control) {
      control.setValue('');
    }
  }

  resetForm() {
    this.form_filtre_demande.reset();
    this.filterApplied.emit(this.form_filtre_demande.value);
    this.cdr.markForCheck();
  }

  applyFilters() {
    this.filterApplied.emit(this.form_filtre_demande.value);
  }
}
